import PublicNav from "../../components/PublicNav";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";

const SECTION_ID = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const SECTIONS = [
  {
    title: "1. Who we are",
    body: `Mystery Served LLC ("Mystery Served," "we," "us," or "our") operates immersive mystery tour experiences in St. Petersburg, Florida and through our website https://mysteryserved.com. This Privacy Policy explains what information we collect, how we use it, who we share it with, and the choices you have. By using our site or booking a tour, you agree to the practices described below.`,
  },
  {
    title: "2. What we collect",
    body: `When you book a tour, sign up for our newsletter, or contact us, we may collect:
• Identity & contact: name, email address, mobile phone number, billing address.
• Booking details: tour selection, date/time, guest count, dietary notes, party notes, names and contact info of co-guests you invite to split payment.
• Payment information: payment is processed by Stripe — we do not store full card numbers on our servers. We retain only the last 4 digits, card brand, and a Stripe transaction ID for receipt and refund purposes.
• Marketing preferences: whether you opted in to receive newsletters, SMS reminders, and promotional messages.
• Usage data: browser type, pages viewed, referring URL, timestamps, and IP address — collected automatically through standard server logs and analytics tools.
• Communications: emails, SMS replies, and support messages you send us.`,
  },
  {
    title: "3. How we use your information",
    body: `We use the information we collect to:
• Fulfill your booking — confirm reservations, send game links, deliver tour-day instructions, process payments, issue receipts and refunds.
• Send transactional messages — booking confirmations, payment links to co-guests, tour reminders, schedule changes, and post-tour follow-ups. These are necessary to deliver the service you purchased.
• Send marketing communications — only if you have explicitly opted in. You can unsubscribe at any time.
• Operate and improve our service — analyze site traffic, prevent fraud, debug issues, and improve the booking experience.
• Comply with legal obligations — tax records, accounting, and lawful requests.`,
  },
  {
    title: "4. SMS messaging program (A2P 10DLC)",
    body: `Mystery Served operates an SMS messaging program through Twilio. The program is used to send booking-related and account notification messages to customers who provide a mobile phone number during the booking process.

Program name: Mystery Served booking and reminder messages.

Message types you may receive:
• Booking confirmations and payment receipts
• Payment link invitations for co-guests on split-pay reservations
• Tour-day game link delivery
• Tour reminders (24-hour and same-day)
• Schedule changes, cancellations, and refunds
• Replies from our team to questions you send us

Message frequency: Varies based on your bookings. You will not receive marketing SMS unless you explicitly opt in to that separately.

Costs: Message and data rates may apply. Mystery Served does not charge for SMS — your mobile carrier may.

How to opt in: You opt in by entering your mobile phone number on our booking form at https://mysteryserved.com/checkout and completing a reservation, or by replying to a co-guest invite. Consent to receive SMS is not a condition of purchase of any goods or services — you may decline SMS by leaving the phone field blank and using email-only communication for co-guests.

How to opt out: Reply STOP to any message we send you. You will receive a single confirmation message and will not be contacted again via SMS. To resubscribe, reply START.

How to get help: Reply HELP to any message, or email info@mysteryserved.com. Our support phone is published in our site footer.

Carriers are not liable for delayed or undelivered messages.

We do not share mobile opt-in information or phone numbers collected as part of our SMS program with third parties or affiliates for marketing or promotional purposes. Phone numbers are only shared with our SMS delivery vendor (Twilio) and our payment processor (Stripe) as necessary to operate the service.`,
  },
  {
    title: "5. Email messaging program",
    body: `We send transactional emails (booking confirmations, payment receipts, tour reminders, refunds) to the email address you provide at checkout. These are necessary to deliver the service.

We may also send occasional marketing emails (newsletters, new tour announcements, promotions) — only if you opt in via our newsletter signup or check the marketing opt-in box at checkout. Every marketing email contains a one-click unsubscribe link. You can also reply with the word UNSUBSCRIBE or email info@mysteryserved.com to be removed.`,
  },
  {
    title: "6. How we share your information",
    body: `We do not sell, rent, or trade your personal information to third parties for their marketing purposes. Ever.

We share information only with the service providers we rely on to operate Mystery Served, and only as needed to deliver the service:
• Stripe — payment processing
• Twilio — SMS delivery
• Resend and Google (Gmail SMTP) — email delivery
• MongoDB Atlas — secure database hosting
• Google Analytics (if enabled) — anonymized site analytics

Each of these vendors is contractually bound to protect your information and use it only to provide their service to us.

We may also disclose information if required by law, valid legal process, or to protect the rights, property, or safety of Mystery Served, our customers, or others.`,
  },
  {
    title: "7. Data retention",
    body: `We retain booking records (including name, email, phone, tour history, and payment metadata) for as long as your account/booking history is active, and for a minimum of 7 years thereafter for tax, accounting, and legal compliance.

Marketing preferences (opt-in/opt-out status) are retained indefinitely so we can honor your choice.

You may request earlier deletion of marketing data at any time — see Your Rights below.`,
  },
  {
    title: "8. Your rights",
    body: `You have the right to:
• Access — request a copy of the personal information we hold about you.
• Correct — ask us to update or correct inaccurate information.
• Delete — request deletion of your information (subject to legal retention requirements above).
• Opt out — unsubscribe from marketing email any time; reply STOP to any SMS.
• Object / Restrict — limit how we use your information for marketing.

To exercise any of these rights, email info@mysteryserved.com. We will respond within 30 days.

If you are a California resident, you also have rights under the California Consumer Privacy Act (CCPA), including the right to know what we collect and the right to non-discrimination for exercising your privacy rights. We do not sell personal information as defined by CCPA.`,
  },
  {
    title: "9. Cookies & analytics",
    body: `We use a small number of cookies and similar technologies to keep you logged in, remember your booking-in-progress, and measure aggregate site traffic. We do not use cookies for cross-site advertising or behavioral targeting.

You can disable cookies in your browser settings; some site features (such as completing a booking) may not work correctly without them.`,
  },
  {
    title: "10. Children",
    body: `Mystery Served is intended for adults. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact info@mysteryserved.com and we will delete it.`,
  },
  {
    title: "11. Security",
    body: `We use industry-standard safeguards — TLS encryption in transit, encrypted database storage, scoped access controls, and PCI-compliant payment processing through Stripe — to protect your information. No method of transmission over the Internet is 100% secure, but we take protection of your data seriously.`,
  },
  {
    title: "12. Changes to this policy",
    body: `We may update this Privacy Policy from time to time. The "Last updated" date at the top will reflect the most recent revision. If we make material changes, we will notify you by email (if you have given us one) or by prominent notice on our site before the changes take effect.`,
  },
  {
    title: "13. Contact us",
    body: `Questions about this Privacy Policy or our data practices? We'd love to hear from you.

Mystery Served LLC
Email: info@mysteryserved.com
Web: https://mysteryserved.com
Mail: see our site footer for our current mailing address.`,
  },
];

export default function Privacy() {
  const lastUpdated = "February 21, 2026";

  return (
    <div className="public-shell">
      <Seo
        title="Privacy Policy"
        description="How Mystery Served collects, uses, and protects your personal information. Includes SMS messaging program disclosures (STOP, HELP, no-sale clause)."
        url="https://mysteryserved.com/privacy"
      />
      <PublicNav />

      {/* HERO */}
      <section className="bg-ms-cream pt-40 md:pt-48 pb-12" data-testid="privacy-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center animate-fade-up">
          <p className="label-eyebrow text-ms-brass" data-testid="privacy-eyebrow">Privacy</p>
          <h1 className="serif text-ms-ink text-5xl md:text-6xl mt-5 leading-[0.95]" data-testid="privacy-headline">
            Privacy <span className="italic text-ms-brass">Policy</span>
          </h1>
          <p className="mt-6 text-ms-stone text-sm uppercase tracking-[0.28em]" data-testid="privacy-last-updated">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-ms-cream pb-24" data-testid="privacy-body">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* TOC */}
          <nav className="bg-white border border-ms-edge rounded-2xl p-6 md:p-8 mb-12" data-testid="privacy-toc">
            <p className="label-eyebrow text-ms-brass">Contents</p>
            <ul className="mt-4 space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.title}>
                  <a
                    href={`#${SECTION_ID(s.title)}`}
                    className="text-ms-ink/80 hover:text-ms-brass underline-offset-4 hover:underline"
                    data-testid={`privacy-toc-${SECTION_ID(s.title)}`}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {SECTIONS.map((s) => (
            <article key={s.title} id={SECTION_ID(s.title)} className="mb-12 scroll-mt-32" data-testid={`privacy-section-${SECTION_ID(s.title)}`}>
              <h2 className="serif text-ms-ink text-2xl md:text-3xl leading-[1.15]">
                {s.title}
              </h2>
              <div className="mt-5 text-ms-stone text-base leading-[1.8] whitespace-pre-line">
                {s.body}
              </div>
            </article>
          ))}

          <div className="hairline my-12" />
          <p className="text-ms-stone text-sm leading-[1.7]">
            For questions about this policy, contact us at{" "}
            <a href="mailto:info@mysteryserved.com" className="text-ms-brass hover:underline">
              info@mysteryserved.com
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
